/**
 * Book Detail — one wedding book, in depth.
 *
 * Three requests in parallel (detail / guests / activity), each with its own
 * failure state, so a slow activity query never blanks the guest list.
 *
 * EVERY MUTATION LIVES HERE, and every one of them is behind a dialog confirm:
 * the dialogs are pure presentation and call back with a value. That is the
 * Quill confirm-gate invariant, and __e2e__/mutations.journey.test.tsx asserts
 * no request is issued until Confirm is pressed.
 */

import React from 'react';
import { Box, Grid, Snackbar, Alert, Skeleton, Stack } from '@mui/material';
import { useParams } from 'react-router-dom';
import EmptyState from '../../common/EmptyState';
import BookStatsHeader from './BookStatsHeader';
import GuestsTable from './GuestsTable';
import PurchasesCard from './PurchasesCard';
import ActivityTimeline from './ActivityTimeline';
import SuspendReactivateDialog, { SuspendMode } from './SuspendReactivateDialog';
import ExtendDeadlineDialog from './ExtendDeadlineDialog';
import ConversionFunnelCard from '../overview/ConversionFunnelCard';
import { csvUrl, startCsvDownload } from '../csvDownload';
import { weddingApi, WeddingApiError } from '../weddingApi';
import type { ActivityEvent, BookDetail, EngagementTracking, GuestRow } from '../weddingApi';

const BookDetailPage: React.FC = () => {
  const { bookId = '' } = useParams<{ bookId: string }>();

  const [detail, setDetail] = React.useState<BookDetail | null>(null);
  const [guests, setGuests] = React.useState<GuestRow[]>([]);
  const [maxReminders, setMaxReminders] = React.useState(3);
  // Undefined until the server answers, and the grid reads undefined as
  // "not tracked" — so the dots never claim a person did not open an email
  // during the moment before the response lands.
  const [guestTracking, setGuestTracking] = React.useState<EngagementTracking | undefined>(undefined);
  const [activity, setActivity] = React.useState<ActivityEvent[]>([]);

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [guestsError, setGuestsError] = React.useState<string | null>(null);

  const [statusDialog, setStatusDialog] = React.useState<SuspendMode | null>(null);
  const [deadlineDialog, setDeadlineDialog] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [dialogError, setDialogError] = React.useState<string | null>(null);
  const [resendingId, setResendingId] = React.useState<string | null>(null);
  const [toast, setToast] = React.useState<{ message: string; severity: 'success' | 'error' } | null>(
    null
  );

  const loadDetail = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDetail(await weddingApi.getBook(bookId));
    } catch (e) {
      setError(e instanceof WeddingApiError ? e.message : 'Could not load this wedding book.');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  const loadGuests = React.useCallback(async () => {
    setGuestsError(null);
    try {
      const res = await weddingApi.getBookGuests(bookId);
      setGuests(res.guests || []);
      setMaxReminders(res.max_reminders || 3);
      setGuestTracking(res.engagement_tracking);
    } catch (e) {
      setGuestsError(e instanceof WeddingApiError ? e.message : 'Could not load the guest list.');
    }
  }, [bookId]);

  const loadActivity = React.useCallback(async () => {
    try {
      const res = await weddingApi.getBookActivity(bookId, 50);
      setActivity(res.events || []);
    } catch {
      // The feed is the least important thing on the page; an empty one with a
      // working guest list beats a whole page of error.
      setActivity([]);
    }
  }, [bookId]);

  const loadAll = React.useCallback(() => {
    loadDetail();
    loadGuests();
    loadActivity();
  }, [loadDetail, loadGuests, loadActivity]);

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  const closeDialogs = () => {
    setStatusDialog(null);
    setDeadlineDialog(false);
    setDialogError(null);
  };

  const confirmStatusChange = async (reason: string) => {
    if (!statusDialog) return;
    setSubmitting(true);
    setDialogError(null);
    try {
      if (statusDialog === 'suspend') {
        const res = await weddingApi.suspendBook(bookId, reason);
        setToast({ message: `Book suspended (was ${res.previous_status}).`, severity: 'success' });
      } else {
        const res = await weddingApi.reactivateBook(bookId);
        setToast({ message: `Book reactivated as ${res.restored_status}.`, severity: 'success' });
      }
      closeDialogs();
      loadDetail();
      loadActivity();
    } catch (e) {
      setDialogError(e instanceof WeddingApiError ? e.message : 'The change could not be saved.');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeadline = async (deadlineLocal: string) => {
    setSubmitting(true);
    setDialogError(null);
    try {
      await weddingApi.setDeadline(bookId, deadlineLocal);
      setToast({ message: 'Deadline updated.', severity: 'success' });
      closeDialogs();
      loadDetail();
      loadActivity();
    } catch (e) {
      setDialogError(e instanceof WeddingApiError ? e.message : 'The deadline could not be saved.');
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async (guest: GuestRow) => {
    setResendingId(guest.id);
    try {
      const res = await weddingApi.resendInvitation(guest.id);
      setToast({
        message:
          res.action === 'invitation'
            ? `Invitation sent to ${guest.first_name}.`
            : `Reminder ${res.reminder_count ?? ''} of ${res.max_reminders} sent to ${guest.first_name}.`,
        severity: 'success',
      });
      loadGuests();
      loadActivity();
    } catch (e) {
      setToast({
        message: e instanceof WeddingApiError ? e.message : 'The email could not be sent.',
        severity: 'error',
      });
    } finally {
      setResendingId(null);
    }
  };

  if (loading && !detail) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Skeleton variant="rounded" height={220} />
          <Skeleton variant="rounded" height={420} />
        </Stack>
      </Box>
    );
  }

  if (error || !detail) {
    return (
      <Box sx={{ p: 3 }}>
        <EmptyState
          icon="error"
          title="This wedding book could not be loaded"
          description={error || 'The book was not found.'}
          action={{ label: 'Retry', onClick: loadAll, variant: 'contained' }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <BookStatsHeader
        detail={detail}
        onSuspend={() => setStatusDialog('suspend')}
        onReactivate={() => setStatusDialog('reactivate')}
        onExtendDeadline={() => setDeadlineDialog(true)}
      />

      {/*
        THE GUEST LIST TAKES THE FULL WIDTH, and the two summary cards sit under
        it rather than beside it.

        It was 8/4, which reads well on paper and does not survive contact with a
        1440px screen: the guest grid's six columns need 890px and an 8-of-12
        column at 1440 gives it 683, so 207px — the whole Resend column, the only
        control in this section that WRITES — sat off the right edge behind a
        horizontal scrollbar. The Purchases table lost its "When" column the same
        way, and worse, because common/DataTable's container is `overflow:
        hidden`: no scrollbar, no way to reach it at all.

        Nothing here is narrow enough to be a sidebar card. Full width for the
        grid, then two half-width cards, and every column is reachable at 1440.
      */}
      <Grid container spacing={3}>
        <Grid size={12}>
          {guestsError ? (
            <EmptyState
              icon="error"
              title="Could not load the guest list"
              description={guestsError}
              action={{ label: 'Retry', onClick: loadGuests, variant: 'contained' }}
            />
          ) : (
            <GuestsTable
              guests={guests}
              maxReminders={maxReminders}
              tracking={guestTracking}
              bookStatus={detail.book.status}
              resendingId={resendingId}
              onResend={resend}
              /* The server names the file after the wedding code; the client
                 only has to say which book. */
              onExportCsv={() => startCsvDownload(
                csvUrl(`/books/${encodeURIComponent(bookId)}/guests/export.csv`)
              )}
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <PurchasesCard purchases={detail.purchases} revenueCents={detail.revenue_cents} />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <ConversionFunnelCard funnel={detail.conversions} title="Cross-sell from this book" />
        </Grid>
        <Grid size={12}>
          <ActivityTimeline events={activity} timezone={detail.book.couple_timezone} />
        </Grid>
      </Grid>

      <SuspendReactivateDialog
        open={statusDialog !== null}
        mode={statusDialog || 'suspend'}
        weddingCode={detail.book.wedding_code}
        currentStatus={detail.book.status}
        submitting={submitting}
        error={dialogError}
        onClose={closeDialogs}
        onConfirm={confirmStatusChange}
      />

      <ExtendDeadlineDialog
        open={deadlineDialog}
        weddingCode={detail.book.wedding_code}
        currentDeadline={detail.book.contribution_deadline}
        coupleTimezone={detail.book.couple_timezone}
        submitting={submitting}
        error={dialogError}
        onClose={closeDialogs}
        onConfirm={confirmDeadline}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity || 'success'} onClose={() => setToast(null)}>
          {toast?.message || ''}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BookDetailPage;
